/**
 * Create Welcome Popover
 * Vanilla JS speech-bubble that appears above the trigger button
 * to proactively greet new visitors and encourage engagement.
 */

import type { LoaderConfig } from './types';

const STORAGE_KEY = 'savanto_popover_dismissed';

interface PopoverPositionConfig {
  bottom?: string;
  top?: string;
  left?: string;
  right?: string;
  transformOrigin: string;
  arrowOffset: string;
  arrowDirection: 'down' | 'up';
}

const DEFAULT_POPOVER_POSITION: PopoverPositionConfig = {
  bottom: '90px',
  right: '20px',
  transformOrigin: 'bottom right',
  arrowOffset: 'right: 26px; bottom: -6px;',
  arrowDirection: 'down',
};

const POPOVER_POSITIONS: Record<string, PopoverPositionConfig> = {
  'bottom-right': DEFAULT_POPOVER_POSITION,
  'bottom-left': {
    bottom: '90px',
    left: '20px',
    transformOrigin: 'bottom left',
    arrowOffset: 'left: 26px; bottom: -6px;',
    arrowDirection: 'down',
  },
  'top-right': {
    top: '90px',
    right: '20px',
    transformOrigin: 'top right',
    arrowOffset: 'right: 26px; top: -6px;',
    arrowDirection: 'up',
  },
  'top-left': {
    top: '90px',
    left: '20px',
    transformOrigin: 'top left',
    arrowOffset: 'left: 26px; top: -6px;',
    arrowDirection: 'up',
  },
};

export interface PopoverAPI {
  /** Show the popover (with entrance animation) */
  show: () => void;
  /** Hide the popover (with exit animation) */
  hide: () => void;
  /** Start the auto-show timer */
  scheduleShow: () => void;
  /** Cancel pending auto-show timer */
  cancelSchedule: () => void;
  /** Remove from DOM and clean up */
  destroy: () => void;
  /** Update position/styles if config changes */
  updateStyles: (config: LoaderConfig) => void;
}

/**
 * Create a welcome popover element.
 * Returns null if popover is disabled, no message is set, or it's been dismissed this session.
 */
export function createPopover(config: LoaderConfig, onClickMessage: () => void): PopoverAPI | null {
  // Don't create popover in inline mode
  if (config.embedMode === 'inline') {
    return null;
  }

  // Check if popover is enabled and has a message
  if (!config.popoverEnabled || !config.popoverMessage) {
    return null;
  }

  // Check if already dismissed this session
  const showOnce = config.popoverShowOnce !== false; // default: true
  if (showOnce && hasBeenDismissed()) {
    return null;
  }

  const delay = config.popoverDelay ?? 3000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let isVisible = false;

  // Create popover container
  const popover = document.createElement('div');
  popover.className = 'savanto-popover';
  popover.setAttribute('role', 'status');
  popover.setAttribute('aria-live', 'polite');
  popover.setAttribute('data-sv-component', 'welcome-popover');

  applyPopoverStyles(popover, config);

  // Message text (clickable to open widget)
  const messageEl = document.createElement('div');
  messageEl.setAttribute('data-sv-component', 'popover-message');
  messageEl.style.cssText = `
    cursor: pointer;
    flex: 1;
    font-size: 14px;
    line-height: 1.45;
  `;
  messageEl.textContent = config.popoverMessage;
  messageEl.onclick = (e) => {
    e.stopPropagation();
    dismiss();
    onClickMessage();
  };

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Dismiss');
  closeBtn.setAttribute('data-sv-component', 'popover-close');
  closeBtn.style.cssText = `
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    margin: -2px -2px -2px 8px;
    color: inherit;
    opacity: 0.5;
    transition: opacity 0.15s ease;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  `;
  closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  closeBtn.onmouseenter = () => {
    closeBtn.style.opacity = '1';
  };
  closeBtn.onmouseleave = () => {
    closeBtn.style.opacity = '0.5';
  };
  closeBtn.onclick = (e) => {
    e.stopPropagation();
    dismiss();
  };

  const arrow = document.createElement('div');
  arrow.setAttribute('data-sv-component', 'popover-arrow');
  const position = config.triggerPosition || 'bottom-right';
  const posConfig = getPositionConfig(position);
  applyArrowStyles(arrow, posConfig, config);

  // Assemble
  popover.appendChild(messageEl);
  popover.appendChild(closeBtn);
  popover.appendChild(arrow);

  // Slide direction: popover slides away from the trigger button
  const slideFrom = posConfig.arrowDirection === 'down' ? 'translateY(8px)' : 'translateY(-8px)';

  // Inject keyframes for idle floating animation (once)
  if (!document.getElementById('sv-popover-keyframes')) {
    const keyframesStyle = document.createElement('style');
    keyframesStyle.id = 'sv-popover-keyframes';
    keyframesStyle.textContent = `
      @keyframes sv-popover-float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-3px); }
      }
    `;
    document.head.appendChild(keyframesStyle);
  }

  // Start hidden
  popover.style.display = 'none';
  popover.style.opacity = '0';
  popover.style.transform = `scale(0.92) ${slideFrom}`;

  // Append to body
  document.body.appendChild(popover);

  function show(): void {
    if (isVisible) return;
    isVisible = true;

    // Reset to initial state
    popover.style.animation = 'none';
    popover.style.transition = 'none';
    popover.style.display = 'flex';
    popover.style.opacity = '0';
    popover.style.transform = `scale(0.92) ${slideFrom}`;

    // Force reflow so the browser registers the initial state
    popover.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions

    // Now apply the transition to final state
    popover.style.transition =
      'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
    popover.style.opacity = '1';
    popover.style.transform = 'scale(1) translateY(0)';

    // After entrance completes, start gentle floating idle
    setTimeout(() => {
      if (isVisible) {
        popover.style.transition = 'none';
        popover.style.animation = 'sv-popover-float 3s ease-in-out infinite';
      }
    }, 450);
  }

  function hide(): void {
    if (!isVisible) return;
    isVisible = false;

    popover.style.animation = 'none';
    popover.style.transition = 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.4, 0, 1, 1)';
    popover.style.opacity = '0';
    popover.style.transform = `scale(0.95) ${slideFrom}`;

    setTimeout(() => {
      popover.style.display = 'none';
    }, 250);
  }

  function dismiss(): void {
    hide();
    cancelSchedule();
    if (showOnce) {
      markDismissed();
    }
  }

  function scheduleShow(): void {
    cancelSchedule();
    timer = setTimeout(() => {
      show();
    }, delay);
  }

  function cancelSchedule(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function destroy(): void {
    cancelSchedule();
    popover.remove();
  }

  function updateStyles(newConfig: LoaderConfig): void {
    applyPopoverStyles(popover, newConfig);
    if (newConfig.popoverMessage) {
      messageEl.textContent = newConfig.popoverMessage;
    }
    const newPosition = newConfig.triggerPosition || 'bottom-right';
    const newPosConfig = getPositionConfig(newPosition);
    applyArrowStyles(arrow, newPosConfig, newConfig);
  }

  return {
    show,
    hide,
    scheduleShow,
    cancelSchedule,
    destroy,
    updateStyles,
  };
}

function getPositionConfig(position: string): PopoverPositionConfig {
  return POPOVER_POSITIONS[position] ?? DEFAULT_POPOVER_POSITION;
}

/** Resolve whether to use dark colors based on colorScheme config */
function isDarkMode(config: LoaderConfig): boolean {
  const scheme = config.colorScheme || 'auto';
  if (scheme === 'dark') return true;
  if (scheme === 'light') return false;
  // Auto: check system preference
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

interface PopoverColors {
  bg: string;
  text: string;
  border: string;
  shadow: string;
}

function getPopoverColors(config: LoaderConfig): PopoverColors {
  if (isDarkMode(config)) {
    return {
      bg: '#2d2d2d',
      text: '#e8eaed',
      border: 'rgba(255, 255, 255, 0.1)',
      shadow: 'rgba(0, 0, 0, 0.3)',
    };
  }
  return {
    bg: '#ffffff',
    text: '#1a1a2e',
    border: 'rgba(0, 0, 0, 0.08)',
    shadow: 'rgba(0, 0, 0, 0.12)',
  };
}

function applyPopoverStyles(el: HTMLElement, config: LoaderConfig): void {
  const position = config.triggerPosition || 'bottom-right';
  const posConfig = getPositionConfig(position);
  const colors = getPopoverColors(config);

  el.style.cssText = `
    position: fixed;
    ${posConfig.bottom ? `bottom: ${posConfig.bottom};` : ''}
    ${posConfig.top ? `top: ${posConfig.top};` : ''}
    ${posConfig.left ? `left: ${posConfig.left};` : ''}
    ${posConfig.right ? `right: ${posConfig.right};` : ''}
    z-index: 999997;
    max-width: 280px;
    padding: 14px 16px;
    background: ${colors.bg};
    color: ${colors.text};
    border: 1px solid ${colors.border};
    border-radius: 14px;
    box-shadow: 0 4px 24px ${colors.shadow}, 0 1px 4px rgba(0, 0, 0, 0.06);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.45;
    display: flex;
    align-items: flex-start;
    gap: 4px;
    transform-origin: ${posConfig.transformOrigin};
    pointer-events: auto;
  `;
}

function applyArrowStyles(arrow: HTMLElement, posConfig: PopoverPositionConfig, config: LoaderConfig): void {
  const colors = getPopoverColors(config);
  const isUp = posConfig.arrowDirection === 'up';

  arrow.style.cssText = `
    position: absolute;
    ${posConfig.arrowOffset}
    width: 12px;
    height: 12px;
    background: ${colors.bg};
    ${isUp ? `border-left: 1px solid ${colors.border}; border-top: 1px solid ${colors.border};` : `border-right: 1px solid ${colors.border}; border-bottom: 1px solid ${colors.border};`}
    transform: rotate(45deg);
    pointer-events: none;
  `;
}

function hasBeenDismissed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // sessionStorage might not be available
  }
}
